import React, { useState, useEffect, useContext } from 'react'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Trash2, Download, Grid, List } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'

const PushedSurgicalSets = () => {
  const { API } = useContext(AuthContext)
  const [surgicalSets, setSurgicalSets] = useState([])
  const [excelDownloads, setExcelDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('cards')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [setsRes, downloadsRes] = await Promise.all([
        axios.get(`${API}/surgical-sets?pushed=true`),
        axios.get(`${API}/surgical-excel-downloads`)
      ])
      setSurgicalSets(setsRes.data)
      setExcelDownloads(downloadsRes.data)
    } catch (error) {
      console.error('Failed to fetch data:', error)
      toast.error('Failed to load pushed surgical sets')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSet = async (setId) => {
    if (!window.confirm('Are you sure you want to delete this pushed surgical set?')) return

    try {
      await axios.delete(`${API}/surgical-sets/${setId}`)
      toast.success('Surgical set deleted')
      fetchData()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete surgical set')
    }
  }

  const handleDeleteDownload = async (downloadId) => {
    if (!window.confirm('Are you sure you want to delete this download record?')) return

    try {
      await axios.delete(`${API}/surgical-excel-downloads/${downloadId}`)
      toast.success('Download record deleted')
      fetchData()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete download record')
    }
  }

  if (loading) {
    return (
      <Layout title="Pushed Surgical Excel">
        <div className="card">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title="Pushed Surgical Excel">
      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Pushed Surgical Data</h2>
          <div className="flex gap-1 bg-gray-100 rounded p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-4 py-2 rounded ${viewMode === 'cards' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Grid className="w-4 h-4 inline mr-2" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <List className="w-4 h-4 inline mr-2" />
              List
            </button>
          </div>
        </div>

        <Tabs defaultValue="sets" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="sets">Surgical Sets ({surgicalSets.length})</TabsTrigger>
            <TabsTrigger value="downloads">Excel Downloads ({excelDownloads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="sets">
            {surgicalSets.length === 0 ? (
              <p className="text-center text-gray-600 py-8">No pushed surgical sets found</p>
            ) : (
              <>
                {viewMode === 'cards' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {surgicalSets.map(surgicalSet => (
                      <div key={surgicalSet.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <h3 className="font-semibold text-base text-gray-900 mb-3">{surgicalSet.set_name}</h3>
                        <div className="mb-3">
                          <p className="text-sm text-gray-600">
                            {surgicalSet.surgical_items.length} surgical item(s)
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Pushed: {new Date(surgicalSet.pushed_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex justify-end">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteSet(surgicalSet.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {surgicalSets.map(surgicalSet => (
                      <div key={surgicalSet.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg text-gray-900">{surgicalSet.set_name}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {surgicalSet.surgical_items.length} surgical item(s)
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Pushed: {new Date(surgicalSet.pushed_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteSet(surgicalSet.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="downloads">
            {excelDownloads.length === 0 ? (
              <p className="text-center text-gray-600 py-8">No Excel downloads found</p>
            ) : (
              <div className="space-y-4">
                {excelDownloads.map(download => (
                  <div key={download.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{download.filename}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {download.surgical_set_ids.length} surgical set(s) included
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Downloaded: {new Date(download.downloaded_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteDownload(download.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  )
}

export default PushedSurgicalSets
